import styled, { keyframes } from "styled-components";
import COLORS from "shared/constants/colors";

const galleryFadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const galleryFloat = keyframes`
  0%, 100% {
    transform: scale(1) translateY(0);
  }
  50% {
    transform: scale(1.03) translateY(-8px);
  }
`;

const galleryShimmer = keyframes`
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(120%);
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 0 20px 30px;
  overflow-x: clip;

  @media (max-width: 768px) {
    padding: 0 12px 20px;
  }
`;

const PageCard = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  background-color: ${COLORS.WHITE};
  border-radius: 8px;
  padding: 24px;
  margin-top: 10px;
  overflow-x: clip;

  @media (max-width: 768px) {
    padding: 16px;
    margin-top: 0;
    border-radius: 6px;
  }
`;

const PageLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 20px;
  align-items: start;
  width: 100%;
  min-width: 0;

  @media (min-width: 1025px) {
    grid-template-columns: minmax(0, 420px) minmax(0, 1fr);
    gap: 28px;
  }

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const GalleryColumn = styled.div`
  position: static;
  min-width: 0;
  width: 100%;

  @media (min-width: 1025px) {
    position: sticky;
    top: 20px;
  }
`;

const ContentColumn = styled.div`
  min-width: 0;
  width: 100%;
`;

const GalleryWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(135deg, #f9fafb 0%, #eef2f7 100%);
  box-shadow: 0 4px 20px rgba(21, 148, 219, 0.08);
  transition: box-shadow 0.35s ease;

  &:hover {
    box-shadow: 0 8px 28px rgba(21, 148, 219, 0.14);
  }

  .slick-slider {
    margin-bottom: 0;
    width: 100%;
    max-width: 100%;
  }

  .slick-list {
    overflow: hidden;
    min-height: 360px;
  }

  .slick-track {
    display: flex;
    align-items: stretch;
  }

  .slick-slide {
    height: auto;
    min-width: 0;

    > div {
      height: 100%;
    }
  }

  .slick-slide > div {
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .slick-dots {
    bottom: 12px;

    li button:before {
      font-size: 10px;
      color: ${COLORS.WHITE};
      opacity: 0.6;
    }

    li.slick-active button:before {
      color: ${COLORS.WHITE};
      opacity: 1;
    }
  }

  .slick-prev,
  .slick-next {
    z-index: 1;
    width: 32px;
    height: 32px;

    &:before {
      font-size: 32px;
      color: ${COLORS.WHITE};
      opacity: 0.9;
    }
  }

  .slick-prev {
    left: 12px;
  }

  .slick-next {
    right: 12px;
  }

  @media (max-width: 768px) {
    .slick-list {
      min-height: 240px;
    }

    .slick-prev,
    .slick-next {
      width: 28px;
      height: 28px;

      &:before {
        font-size: 28px;
      }
    }
  }
`;

const GallerySlide = styled.div`
  position: relative;
  outline: none;
  overflow: hidden;
  width: 100%;
  max-width: 100%;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      105deg,
      transparent 40%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 60%
    );
    transform: translateX(-120%);
    pointer-events: none;
    z-index: 1;
  }

  .slick-active &::after {
    animation: ${galleryShimmer} 5s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

const GalleryImage = styled.img`
  display: block;
  width: 100%;
  max-width: 100%;
  height: 360px;
  object-fit: cover;
  transform-origin: center center;

  .slick-active & {
    animation: ${galleryFadeIn} 0.6s ease forwards,
      ${galleryFloat} 5s ease-in-out 0.6s infinite;
  }

  @media (max-width: 768px) {
    height: 240px;
  }

  @media (prefers-reduced-motion: reduce) {
    .slick-active & {
      animation: none;
    }
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 32px;
  line-height: 1.2;
  font-family: "Inter600";
  color: #18191f;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const VividBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-family: "Inter600";
  color: ${COLORS.WHITE};
  background: linear-gradient(90deg, #7b2ff7 0%, #22c55e 100%);
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const PricingLine = styled.p`
  margin: 0 0 20px;
  font-size: 15px;
  color: #4b5563;
  word-break: break-word;

  strong {
    color: ${COLORS.BLUE};
    font-family: "Inter600";
  }

  img {
    width: 18px;
    height: 12px;
    margin: 0 4px;
    vertical-align: middle;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 16px;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 24px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #18191f;
  font-family: "Inter500";

  svg {
    color: ${COLORS.BLUE};
    font-size: 22px;
  }
`;

const FeatureVideoLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: #6b7280;
  font: inherit;
  font-family: "Inter400";
  text-decoration: none;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  &:not(:disabled):hover {
    color: ${COLORS.BLUE};
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const PromoBar = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 14px 0;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PromoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #18191f;
  font-family: "Inter500";

  strong {
    color: ${COLORS.BLUE};
    font-family: "Inter600";
  }

  svg {
    color: ${COLORS.BLUE};
    font-size: 20px;
  }
`;

const UploadSection = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const UploadSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;

  h2 {
    margin: 0;
    font-size: 18px;
    font-family: "Inter600";
    color: #18191f;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 14px 16px;
    gap: 12px;

    h2 {
      font-size: 16px;
    }
  }
`;

const UploadTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
`;

const UploadTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: ${({ $active }) => ($active ? "#e8f4fc" : "transparent")};
  color: ${({ $active }) => ($active ? COLORS.BLUE : "#4b5563")};
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-family: "Inter500";

  svg {
    font-size: 18px;
  }
`;

const UploadPanel = styled.div`
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const DropZone = styled.div`
  border: 2px dashed
    ${({ $isDragging }) => ($isDragging ? COLORS.BLUE : "#b8d9f0")};
  background: ${({ $isDragging }) => ($isDragging ? "#f0f8ff" : "#f8fbff")};
  border-radius: 8px;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 20px;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background 0.2s ease;

  @media (max-width: 768px) {
    min-height: 220px;
    padding: 24px 16px;
  }
`;

const UploadIconCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${COLORS.BLUE};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  svg {
    color: ${COLORS.WHITE};
    font-size: 34px;
  }
`;

const DropZoneTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 18px;
  font-family: "Inter600";
  color: #18191f;
`;

const DropZoneSubtitle = styled.p`
  margin: 0 0 20px;
  font-size: 14px;
  color: #6b7280;
`;

const HiddenInput = styled.input`
  display: none;
`;

const PlaceholderPanel = styled.div`
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #6b7280;
  font-size: 15px;
  padding: 24px;
`;

const FileList = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #18191f;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StepTitle = styled.h3`
  margin: 0 0 18px;
  font-size: 18px;
  font-family: "Inter600";
  color: #18191f;

  span {
    color: ${COLORS.BLUE};
  }

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 14px;
  }
`;

const StepCard = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  background: ${COLORS.WHITE};

  @media (max-width: 768px) {
    padding: 14px;
    margin-bottom: 12px;
  }
`;

const CustomizeLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 20px;
  width: 100%;
  min-width: 0;

  @media (min-width: 769px) {
    grid-template-columns: 180px minmax(0, 1fr);
  }

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const AiToolsPanel = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px 14px;
  background: #fafafa;
`;

const AiToolsTitle = styled.p`
  margin: 0 0 14px;
  font-size: 13px;
  font-family: "Inter600";
  color: ${COLORS.BLUE};
`;

const AiToolRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #18191f;
`;

const EditWithAiButton = styled.button`
  width: 100%;
  margin-top: 8px;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  background: ${COLORS.WHITE};
  color: #18191f;
  padding: 12px 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  font-family: "Inter500";

  svg {
    color: #f59e0b;
    font-size: 18px;
  }
`;

const PreviewPanel = styled.div`
  min-width: 0;
`;

const PreviewToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;

  button {
    border: none;
    background: transparent;
    cursor: pointer;
    color: #6b7280;
    padding: 4px;

    svg {
      font-size: 20px;
    }
  }
`;

const PreviewCanvas = styled.div`
  position: relative;
  min-height: 320px;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background-color: #f8fafc;
  background-image:
    linear-gradient(45deg, #eceff1 25%, transparent 25%),
    linear-gradient(-45deg, #eceff1 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #eceff1 75%),
    linear-gradient(-45deg, transparent 75%, #eceff1 75%);
  background-size: 20px 20px;
  background-position:
    0 0,
    0 10px,
    10px -10px,
    -10px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: 220px;
    padding: 24px 16px;
  }
`;

const PreviewImage = styled.img`
  max-width: 180px;
  max-height: 180px;
  object-fit: contain;
  position: relative;
  z-index: 1;
`;

const DimensionGuide = styled.div`
  position: absolute;
  inset: 72px;
  pointer-events: none;
`;

const DimensionLine = styled.div`
  position: absolute;
  background: ${COLORS.BLUE};

  ${({ $horizontal }) =>
    $horizontal
      ? `
    left: 0;
    right: 0;
    top: 50%;
    height: 2px;
    transform: translateY(-50%);
  `
      : `
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    transform: translateX(-50%);
  `}
`;

const DimensionLabel = styled.span`
  position: absolute;
  background: ${COLORS.BLUE};
  color: ${COLORS.WHITE};
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
`;

const PreviewCaption = styled.div`
  margin: 14px auto 0;
  width: fit-content;
  max-width: 100%;
  background: ${COLORS.WHITE};
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 13px;
  color: #4b5563;
  text-align: center;
`;

const SizeTabs = styled.div`
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 12px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const SizeTab = styled.button`
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 0 12px;
  cursor: pointer;
  font-size: 14px;
  font-family: ${({ $active }) => ($active ? '"Inter600"' : '"Inter500"')};
  color: ${({ $active }) => ($active ? "#18191f" : "#9ca3af")};
  border-bottom: 2px solid
    ${({ $active }) => ($active ? "#18191f" : "transparent")};
  margin-bottom: -1px;
  white-space: nowrap;
  flex-shrink: 0;

  svg {
    font-size: 18px;
  }
`;

const SizeHelpText = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  color: #6b7280;

  button {
    border: none;
    background: transparent;
    color: ${COLORS.BLUE};
    cursor: pointer;
    padding: 0;
    font-size: 14px;
    font-family: "Inter500";
  }
`;

const SizeFieldsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const SizeField = styled.div`
  min-width: 0;
  grid-column: ${({ $wide }) => ($wide ? "1 / -1" : "auto")};

  select {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 10px 12px;
    font-size: 14px;
    color: #18191f;
    background: ${COLORS.WHITE};
  }
`;

const SizeFieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  color: #6b7280;
`;

const SizeInput = styled.input`
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
  color: #18191f;
  box-sizing: border-box;
`;

const PriceValue = styled.div`
  min-height: 42px;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #18191f;
  font-family: ${({ $bold }) => ($bold ? '"Inter600"' : '"Inter500"')};
`;

const AddSizeLink = styled.button`
  border: none;
  background: transparent;
  color: ${COLORS.BLUE};
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  font-family: "Inter500";
`;

const CartActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-top: 16px;
    padding-top: 16px;
  }
`;

const CartSuccessMessage = styled.span`
  font-size: 14px;
  color: #16a34a;
  font-family: "Inter500";
`;

const EmptyCartMessage = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 14px;
  font-family: "Inter400";
`;

const CartSummary = styled.div`
  margin-top: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  background: #fafafa;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const CartSummaryTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 18px;
  font-family: "Inter600";
  color: #18191f;
`;

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 72px 1fr auto;
  gap: 16px;
  align-items: start;
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-of-type {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 56px minmax(0, 1fr);
    gap: 12px;
  }
`;

const CartItemImage = styled.img`
  width: 72px;
  height: 72px;
  object-fit: contain;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background:
    linear-gradient(45deg, #eceff1 25%, transparent 25%),
    linear-gradient(-45deg, #eceff1 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #eceff1 75%),
    linear-gradient(-45deg, transparent 75%, #eceff1 75%);
  background-size: 12px 12px;
  background-position:
    0 0,
    0 6px,
    6px -6px,
    -6px 0;

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
  }
`;

const CartItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  color: #18191f;
`;

const CartItemMeta = styled.div`
  color: #6b7280;
  font-size: 13px;
`;

const CartItemTotal = styled.div`
  font-size: ${({ $large }) => ($large ? "16px" : "14px")};
  font-family: "Inter600";
  color: #18191f;
  margin-top: ${({ $large }) => ($large ? "16px" : "4px")};
`;

const RemoveCartItemButton = styled.button`
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;

  svg {
    font-size: 20px;
  }
`;

export {
  Container,
  PageCard,
  PageLayout,
  GalleryColumn,
  ContentColumn,
  GalleryWrapper,
  GallerySlide,
  GalleryImage,
  HeaderRow,
  PageTitle,
  VividBadge,
  PricingLine,
  FeatureGrid,
  FeatureItem,
  FeatureVideoLink,
  PromoBar,
  PromoItem,
  UploadSection,
  UploadSectionHeader,
  UploadTabs,
  UploadTab,
  UploadPanel,
  DropZone,
  UploadIconCircle,
  DropZoneTitle,
  DropZoneSubtitle,
  HiddenInput,
  PlaceholderPanel,
  FileList,
  FileItem,
  StepCard,
  StepTitle,
  CustomizeLayout,
  AiToolsPanel,
  AiToolsTitle,
  AiToolRow,
  EditWithAiButton,
  PreviewPanel,
  PreviewToolbar,
  PreviewCanvas,
  PreviewImage,
  DimensionGuide,
  DimensionLine,
  DimensionLabel,
  PreviewCaption,
  SizeTabs,
  SizeTab,
  SizeHelpText,
  SizeFieldsRow,
  SizeField,
  SizeFieldLabel,
  SizeInput,
  PriceValue,
  AddSizeLink,
  CartActions,
  CartSuccessMessage,
  EmptyCartMessage,
  CartSummary,
  CartSummaryTitle,
  CartItem,
  CartItemImage,
  CartItemDetails,
  CartItemMeta,
  CartItemTotal,
  RemoveCartItemButton,
};
